import { IValueInputAttachable } from "client/Forms/attachable";
import { IntegrationFlowApiKeyAuthenticationFormData } from "../../common";
import { ReactElement } from "react";
import { FormItem, ValidationError } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { PossibleSecretKeyValueCollection } from "../../PossibleSecretKeyValueCollection";
import {
	createValidationConfiguration,
	IObjectValidatorBuilder,
	IValidationConfiguration
} from "client/Validation/builder";
import { getClientId, ObjectWithClientId } from "client/Misc/clientId";
import {
	KeyedValueSaveModel,
	SecretSaveModel,
	StringSaveModel,
	StringSecretDiscriminator
} from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import {
	doesNotExceedLength,
	hasMaximumEntries,
	isNotEmpty
} from "client/Validation/rules";
import { EMPTY_ARRAY } from "client/Misc/utils";

export type ApiKeySettingsProps = {
	attachable: IValueInputAttachable<IntegrationFlowApiKeyAuthenticationFormData>;
};

export const ApiKeySettings = (props: ApiKeySettingsProps): ReactElement => {
	const {
		attachable
	} = props;

	return (
		<>
			<FormItem label={localize("_Headers")} >
				<PossibleSecretKeyValueCollection
					attachable={attachable.for("headers")}
					maxAmount={20}
				/>

				<ValidationError {...attachable.for("headers").attachToValidationError(true)} />
			</FormItem>

			<FormItem label={localize("_QueryParameters")} >
				<PossibleSecretKeyValueCollection
					attachable={attachable.for("queryParameters")}
					maxAmount={20}
				/>

				<ValidationError {...attachable.for("queryParameters").attachToValidationError(true)} />
			</FormItem>
		</>
	);
};
ApiKeySettings.displayName = "ApiKeySettings";

export const defaultApiKeySettings: IntegrationFlowApiKeyAuthenticationFormData = {
	headers: EMPTY_ARRAY,
	queryParameters: EMPTY_ARRAY
};

export const apiKeyValidationConfiguration: IValidationConfiguration<IntegrationFlowApiKeyAuthenticationFormData> = createValidationConfiguration<IntegrationFlowApiKeyAuthenticationFormData>();

const headerConfiguration: IObjectValidatorBuilder<ObjectWithClientId<KeyedValueSaveModel>> = apiKeyValidationConfiguration
	.forCollection("headers")
	.add(
		hasMaximumEntries(20)
	)
	.forItems(getClientId);

headerConfiguration.forScalar("key")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
);

headerConfiguration.forObject("value")
	.whenType((x): x is StringSaveModel => x.kind === StringSecretDiscriminator.String)
	.forScalar("string")
	.add(
		doesNotExceedLength(1_024)
	);

headerConfiguration.forObject("value")
	.whenType((x): x is SecretSaveModel => x.kind === StringSecretDiscriminator.Secret)
	.forObject("secret")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		doesNotExceedLength(1_024)
	);

const queryParametersConfiguration: IObjectValidatorBuilder<ObjectWithClientId<KeyedValueSaveModel>> = apiKeyValidationConfiguration
	.forCollection("queryParameters")
	.add(
		hasMaximumEntries(20)
	)
	.forItems(getClientId);

queryParametersConfiguration.forScalar("key")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
	);

queryParametersConfiguration.forObject("value")
	.whenType((x): x is StringSaveModel => x.kind === StringSecretDiscriminator.String)
	.forScalar("string")
	.add(
		doesNotExceedLength(1_024)
	);

queryParametersConfiguration.forObject("value")
	.whenType((x): x is SecretSaveModel => x.kind === StringSecretDiscriminator.Secret)
	.forObject("secret")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		doesNotExceedLength(1_024)
	);